/**
 * Full-screen overlay panels (title, pause, help, game over, victory).
 *
 * These are real DOM sections rather than canvas drawings so every button is
 * focusable, announced, and reachable by keyboard or touch.
 */

export function createOverlays(root) {
  const panels = new Map();
  root.querySelectorAll('[data-screen]').forEach((panel) => {
    panels.set(panel.dataset.screen, panel);
  });

  let active = null;
  /** Where focus was before the overlay opened, so we can hand it back. */
  let restoreFocus = null;

  function hideAll() {
    panels.forEach((panel) => {
      panel.hidden = true;
    });
    root.hidden = true;
    active = null;
  }

  return {
    get active() {
      return active;
    },

    show(name, fields = {}) {
      const panel = panels.get(name);
      if (!panel) return;

      if (!active && document.activeElement instanceof HTMLElement) {
        restoreFocus = document.activeElement;
      }

      panels.forEach((el) => {
        el.hidden = el !== panel;
      });
      root.hidden = false;
      active = name;

      for (const [key, value] of Object.entries(fields)) {
        const slot = panel.querySelector(`[data-field="${key}"]`);
        if (slot) slot.textContent = value;
      }

      const target = panel.querySelector('[data-autofocus]') || panel.querySelector('button');
      // Wait a frame so the panel is visible before we move focus into it.
      requestAnimationFrame(() => target?.focus({ preventScroll: true }));
    },

    hide() {
      const had = active;
      hideAll();
      if (had && restoreFocus?.isConnected) {
        restoreFocus.focus({ preventScroll: true });
      }
      restoreFocus = null;
    },

    /** Keep focus inside the open panel while it is up. */
    trapFocus(event) {
      if (!active) return;
      const panel = panels.get(active);
      const focusables = panel.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  };
}
